import React, { useCallback, useEffect, useState } from 'react';
import { StaticSearchableSelect } from 'shared/components/select/SearchableSelects';
import { useRequest } from 'shared/hooks';
import { qosEntity } from 'shared/entityDetails';

/**
 * QOS select that owns its options request, so parents don't refetch it on every render.
 * @returns {function({label: string, value?: (string|number|Object), setValue: Function}): *}
 */
export default function withQosSearchableSelect() {
  const getOptionValue = (o) => o.id;

  /**
   * @param {Object} props
   * @param {string} props.label - Label for the select field
   * @param {string|number|Object} [props.value=null] - Current selected value
   * @param {Function} props.setValue - Function to update the value
   */
  function QosStaticSearchableSelectEditor({
    label, value = null, setValue,
  }) {
    const { request } = useRequest();
    const [selectedOption, setSelectedOption] = useState(null);
    const [options, setOptions] = useState(undefined);

    useEffect(() => {
      (async () => {
        const response = await request(qosEntity.getRequest(''));
        if (response) {
          setOptions(response?.rows);
        }
      })();
    }, [request]);

    useEffect(() => {
      const matchedOption = options && options.find((item) => getOptionValue(item) === value);
      if (matchedOption) {
        setSelectedOption(matchedOption);
      } else {
        setSelectedOption(null);
      }
    }, [options, selectedOption, value]);

    const onChange = useCallback((option) => {
      if (!option) {
        setSelectedOption(null);
        setValue(null);
      } else {
        setSelectedOption(option);
        setValue(getOptionValue(option));
      }
    }, [setValue]);

    return (
      <StaticSearchableSelect
        label={label}
        onChange={onChange}
        staticOptions={options || []}
        selectedOption={selectedOption}
        getOptionLabel={qosEntity.getOptionLabel}
        getOptionValue={getOptionValue}
        disableClearable
      />
    );
  }

  return QosStaticSearchableSelectEditor;
}
